const BattleCalc = require('../BattleCalc');
const MoveTowardsDecision = require('./movement/MoveTowardsDecision');
const MoveAwayDecision = require('./movement/MoveAwayDecision');
const DPSCloseDecision = require('./dps_close/DPSCloseDecision');
const DPSRangedDecision = require('./dps_ranged/DPSRangedDecision');
const SupportDecision = require('./support/SupportDecision');
const InterferenceDecision = require('./interference/InterferenceDecision');
const TankDecision = require('./tank/TankDecision');
const { logError } = require('../logger');
const SelfBuffDecision = require('./buff/SelfBuffDecision');

const AIHandler = function (battleState) {
  var _unitDecisions = {};
  this.getUnitDecisions = () => {
    return _unitDecisions;
  };

  this.addUnitDecisions = (unit) => {
    try {
      if (!_unitDecisions) {
        return;
      }

      this.removeUnitDecisions(unit.uid);

      //  breakIncapacitation: new BreakIncapacitationDecision( unit, battleState ),
      //  getBehindTank: new GetBehindTankDecision( unit, battleState ),
      const buff = new SelfBuffDecision(unit, battleState);
      var moveTowards = new MoveTowardsDecision(unit, battleState);
      var moveAway = new MoveAwayDecision(unit, battleState);
      var dps_close = new DPSCloseDecision(unit, battleState, moveTowards);
      var dps_ranged = new DPSRangedDecision(unit, battleState);
      var interference = new InterferenceDecision(unit, battleState);
      var support = new SupportDecision(unit, battleState);
      var tank = new TankDecision(unit, battleState, moveTowards);

      _unitDecisions[unit.uid] = {
        //  breakIncapacitation: new BreakIncapacitationDecision( unit, battleState ),
        //  getBehindTank: new GetBehindTankDecision( unit, battleState ),
        buff,
        moveTowards,
        moveAway,
        dps_close,
        dps_ranged,
        interference,
        support,
        tank,
      };

      for (var i = 0; i < unit.ai.priorities.length; ++i) {
        _unitDecisions[unit.uid][i] = _unitDecisions[unit.uid][unit.ai.priorities[i]];
      }
    } catch (err) {
      logError(err, {
        module: 'AIHandler',
        func: 'addUnitDecisions',
        unit: JSON.stringify(unit),
      });
    }
  };

  this.removeUnitDecisions = (unitId) => {
    for (const decision of Object.values(_unitDecisions[unitId] || {})) {
      decision.dispose();
    }
    delete _unitDecisions[unitId];
  };

  for (const unit of Object.values(battleState.allUnits || {})) {
    this.addUnitDecisions(unit);
  }

  this.getAction = (unitId, blacklistedAbilities) => {
    if (!battleState || !battleState.allPieces) {
      return 'wait';
    }

    const actor = battleState.allPieces[unitId];
    let action = null;

    if (!actor || !actor.ai) {
      return 'wait';
    }

    var enraged = BattleCalc.getCondition(actor, 'enraged');
    if (enraged) {
      var rageTarget = battleState.allUnits[enraged.instigatorId];
      if (!rageTarget || rageTarget.dead || !rageTarget.inPlay) {
        battleState.expireCondition(enraged.uid);
      } else {
        return attemptAction(
          {
            actorId: actor.uid,
            abilityHandle: 'melee_attack',
            victimId: enraged.instigatorId,
          },
          blacklistedAbilities
        );
      }
    }

    var scared = BattleCalc.getCondition(actor, 'scared');
    if (scared) {
      var fearSource = battleState.allUnits[scared.instigatorId];
      if (!fearSource || fearSource.dead || !fearSource.inPlay) {
        battleState.expireCondition(scared.uid);
      } else {
        action = _unitDecisions[unitId].moveAway.query(battleState.allUnits[scared.instigatorId]);
        if (action && !blacklistedAbilities.has(action.abilityHandle)) {
          return attemptAction(action, blacklistedAbilities);
        } else {
          return 'wait';
        }
      }
    }

    if (!Array.isArray(actor.ai.priorities) && typeof actor.ai.priorities === 'object') {
      actor.ai.priorities = Object.values(actor.ai.priorities);
    }

    // in case any of my allies (including myself) is critically low on HP, let's try the support tree one time
    const allies = BattleCalc.getAlliesOfUnit(
      actor,
      battleState.allUnits,
      true,
      false // never heal summons
    );
    for (let i = 0; i < allies.length; ++i) {
      if (BattleCalc.unitHPBelowPercent(allies[i], 15)) {
        for (let j = 0; j < actor.ai.priorities.length; ++j) {
          if (
            actor.ai.priorities[j] === 'support' &&
            (BattleCalc.getEquippedAbility(actor, 'healing_prayer') ||
              BattleCalc.getEquippedAbility(actor, 'sanctuary') ||
              BattleCalc.getEquippedAbility(actor, 'holy_force') ||
              BattleCalc.getEquippedAbility(actor, 'suture'))
          ) {
            try {
              const decision = _unitDecisions[unitId];
              action = decision ? decision[j]?.query() : null;
            } catch (err) {
              logError(err, {
                module: 'AIHandler',
                func: 'attemptAction:critically_low_hp_ally',
                actor: JSON.stringify(actor),
              });
            }
            break;
          }
        }
        break;
      }
    }
    if (action && !blacklistedAbilities.has(action.abilityHandle)) {
      return attemptAction(action, blacklistedAbilities);
    }

    // apply any self-buffs
    try {
      action = _unitDecisions[unitId].buff.query();
      if (action && !blacklistedAbilities.has(action.abilityHandle)) {
        return attemptAction(action, blacklistedAbilities);
      }
    } catch (err) {
      logError(err, {
        module: 'AIHandler',
        func: 'self_buffs',
        action: JSON.stringify(action),
      });
    }

    // go in priority order through our decision trees, looking for an action to take
    for (let i = 0; i < actor.ai.priorities.length; ++i) {
      try {
        const decision = _unitDecisions[unitId];
        action = decision ? decision[actor.ai.priorities[i]].query() : null;
        if (action && !blacklistedAbilities.has(action.abilityHandle)) {
          return attemptAction(action, blacklistedAbilities);
        }
      } catch (err) {
        logError(err, {
          module: 'AIHandler',
          func: 'getAction_waterfall',
          action: JSON.stringify(action),
        });
      }
    }

    // no action found.
    return 'wait';
  };

  function attemptAction(action, blacklistedAbilities) {
    if (!action) throw new Error('invalid action passed to AIHandler::attemptAction');

    const actor = battleState.allPieces[action.actorId];
    var ability = BattleCalc.getEquippedAbility(actor, action.abilityHandle);

    var victim = battleState.allPieces[action.victimId];
    var dist = BattleCalc.boardDistance(actor, victim);

    if (!['move', 'leap', 'teleport'].includes(ability.handle))
      actor.abilityDestination = {
        abilityHandle: ability.handle,
        actorId: actor.uid,
        victimId: victim.uid,
      };

    if (dist > ability.range.max) {
      action = _unitDecisions[actor.uid].moveTowards.query(victim);
      if (!action || blacklistedAbilities.has(action.abilityHandle)) {
        // i'm trying to get somewhere but movement is impossible (i.e. surrounded)
        const target_heroes_only = actor.hero && !actor.poppet && !battleState.isCaverns;
        let nearest_enemy = battleState.getNearestEnemyOf(actor, target_heroes_only);
        if (BattleCalc.adjacent(actor, nearest_enemy)) {
          action = _unitDecisions[actor.uid].dps_close.query(nearest_enemy);
        } else {
          nearest_enemy = battleState.getNearestEnemyOf(actor, false);
          if (BattleCalc.adjacent(actor, nearest_enemy)) {
            action = _unitDecisions[actor.uid].dps_close.query(nearest_enemy);
          } else {
            return doSomethingForGodsSake(
              actor,
              battleState,
              action?.abilityHandle,
              blacklistedAbilities
            );
          }
        }
      }

      ability = BattleCalc.getEquippedAbility(actor, action.abilityHandle);
    } else if (dist < ability.range.min) {
      action = _unitDecisions[actor.uid].moveAway.query(victim);
      if (!action || blacklistedAbilities.has(action.abilityHandle)) {
        if (BattleCalc.getCondition(actor, 'scared')) {
          return 'wait';
        } else {
          // i'm a ranged unit trying to get away from a melee unit, but i can't move away (e.g. surrounded)
          const nearest_enemy = battleState.getNearestEnemyOf(actor);
          if (BattleCalc.adjacent(actor, nearest_enemy)) {
            action = _unitDecisions[actor.uid].dps_close.query(nearest_enemy, true);
          } else {
            return 'wait';
          }
        }
      }
      ability = BattleCalc.getEquippedAbility(actor, action.abilityHandle);
    }

    // victim may have been changed by moveTowards or moveAway (to a tile)
    var victim = battleState.allPieces[action.victimId];

    const blessedFactor = BattleCalc.getBlessedFactor(actor);

    const apCost =
      (ability.handle === 'leap'
        ? BattleCalc.getLeapAPCostByDist(actor, BattleCalc.boardDistance(actor, victim))
        : ability.apCost) * blessedFactor;
    const mpCost =
      (ability.handle === 'teleport'
        ? BattleCalc.getTeleportMPCostByDist(actor, BattleCalc.boardDistance(actor, victim))
        : ability.mpCost) * blessedFactor;

    if ((!ability.apCost || actor.ap >= apCost) && (!ability.mpCost || actor.mp >= mpCost)) {
      return action;
    } else if (actor.ap < apCost) {
      const adrenaline = BattleCalc.getEquippedAbility(actor, 'adrenaline');
      if (
        adrenaline &&
        !BattleCalc.getCondition(actor, 'scared') &&
        actor.mp >= adrenaline.mpCost * blessedFactor
      ) {
        return {
          actorId: actor.uid,
          abilityHandle: 'adrenaline',
          victimId: actor.uid,
        };
      }
    } else if (actor.mp < mpCost) {
      const mysticFocus = BattleCalc.getEquippedAbility(actor, 'mystic_focus');
      if (mysticFocus) {
        if (actor.ap >= mysticFocus.apCost * blessedFactor) {
          return {
            actorId: actor.uid,
            abilityHandle: 'mystic_focus',
            victimId: actor.uid,
          };
        }
      } else if (actor.ap >= actor.max_ap) {
        return doSomethingForGodsSake(
          actor,
          battleState,
          action?.abilityHandle,

          blacklistedAbilities
        );
      }
    }

    return doSomethingForGodsSake(actor, battleState, action?.abilityHandle, blacklistedAbilities);
  }

  function doSomethingForGodsSake(
    actor,
    battleState,
    failed_action_handle,
    blacklistedAbilities,
    { no_kite, no_chase } = {}
  ) {
    if (actor.ap < actor.max_ap) {
      return 'wait';
    }

    let last_ditch_action;

    const target_heroes_only = actor.hero && !actor.poppet && !battleState.isCaverns;
    const nearest_enemy = battleState.getNearestEnemyOf(actor, target_heroes_only);
    const actor_is_pure_ranged = BattleCalc.unitIsPureRanged(actor);

    // squishies kite (move away from nearest enemy)
    if (
      actor_is_pure_ranged &&
      !BattleCalc.isSurrounded(actor, battleState) &&
      !blacklistedAbilities.has('move') &&
      !no_kite
    ) {
      last_ditch_action = _unitDecisions[actor.uid].moveAway.query(
        battleState.getNearestEnemyOf(actor, target_heroes_only)
      );
      if (last_ditch_action) {
        return last_ditch_action;
      } else {
        return doSomethingForGodsSake(
          actor,
          battleState,
          failed_action_handle,
          blacklistedAbilities,
          {
            no_kite: true,
          }
        );
      }
      // tanks and melee DPS move towards nearest enemy
    } else if (
      !actor_is_pure_ranged &&
      !BattleCalc.isSurrounded(actor, battleState) &&
      BattleCalc.boardDistance(actor, nearest_enemy) > 1 &&
      !blacklistedAbilities.has('move') &&
      !no_chase
    ) {
      last_ditch_action = _unitDecisions[actor.uid].moveTowards.query(nearest_enemy);
      if (last_ditch_action) {
        return last_ditch_action;
      } else {
        return doSomethingForGodsSake(
          actor,
          battleState,
          failed_action_handle,
          blacklistedAbilities,
          {
            no_chase: true,
          }
        );
      }
    } else {
      // there is truly nothing this unit can do
      return 'wait';
    }
  }

  this.dispose = () => {
    for (const unitId in _unitDecisions) {
      for (const decision in _unitDecisions[unitId]) {
        _unitDecisions[unitId][decision].dispose();
      }
    }
    _unitDecisions = null;
  };
};
module.exports = AIHandler;
